import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  FileText, 
  BookOpen, 
  MessageSquare, 
  Mail,
  ChevronRight,
  Sparkles,
  Calendar,
  Bell,
  ClipboardCheck,
  AlertCircle,
  CheckCircle2,
  Users,
  X,
  TrendingUp,
  Flame,
  Video,
  Clock,
  ExternalLink,
  CalendarClock,
  Activity,
  Target,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";

const getMeetingCalColors = (meeting, isPending) => {
  if (meeting.meeting_type === 'group') return { bg: 'bg-indigo-50', label: 'text-indigo-500', day: 'text-indigo-700' };
  if (isPending) return { bg: 'bg-amber-50', label: 'text-amber-500', day: 'text-amber-700' };
  return { bg: 'bg-teal-50', label: 'text-teal-500', day: 'text-teal-700' };
};

export default function StudentDashboard({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [checkInStatus, setCheckInStatus] = useState(null);
  const [streakData, setStreakData] = useState(null);
  const [journalNotifications, setJournalNotifications] = useState([]);
  const [checkinReminders, setCheckinReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [teacherId, setTeacherId] = useState(null);

  useEffect(() => {
    // Show success toast if just submitted check-in
    if (location.state?.checkInSubmitted) {
      toast.success("Weekly check-in submitted successfully!");
      // Clear the state to prevent showing toast on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchNotifications = useCallback(async () => {
    try {
      const notificationsRes = await axios.get(`${API}/notifications`);
      const allNotifications = notificationsRes.data || [];
      
      // Filter for journal_reviewed notifications that are unread
      const journalNotifs = allNotifications.filter(
        n => n.type === 'journal_reviewed' && !n.is_read
      );
      setJournalNotifications(journalNotifs);
      
      // Filter for checkin_reminder notifications that are unread
      const checkinNotifs = allNotifications.filter(
        n => n.type === 'checkin_reminder' && !n.is_read
      );
      setCheckinReminders(checkinNotifs);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, []);

  const fetchUnreadMessages = useCallback(async () => {
    try {
      // Get direct messages
      const directRes = await axios.get(`${API}/messages/unread?limit=10`);
      const directMsgs = (directRes.data || []).map(msg => ({...msg, is_group: false}));
      
      // Get group messages
      let groupMsgs = [];
      try {
        const groupRes = await axios.get(`${API}/messages/unread-all`);
        groupMsgs = groupRes.data.group_messages || [];
      } catch (e) {
        console.error("Failed to load group messages:", e);
      }
      
      // Combine and sort
      const allMessages = [...directMsgs, ...groupMsgs].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setUnreadMessages(allMessages);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, assignmentsRes, checkInRes, notificationsRes, streakRes, meetingsRes, teachersRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/assignments`),
        axios.get(`${API}/checkins/status`),
        axios.get(`${API}/notifications`),
        axios.get(`${API}/checkins/streak`),
        axios.get(`${API}/meetings?time_filter=upcoming`),
        axios.get(`${API}/users/teachers`),
      ]);

      setStats(statsRes.data);
      setAssignments(assignmentsRes.data.slice(0, 5));
      setCheckInStatus(checkInRes.data);
      setStreakData(streakRes.data);
      setUpcomingMeetings(
        (meetingsRes.data || [])
          .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
          .slice(0, 3)
      );
      if (teachersRes.data?.length > 0) {
        setTeacherId(teachersRes.data[0].user_id);
      }
      
      const allNotifications = notificationsRes.data || [];
      // Filter for journal_reviewed notifications that are unread
      const journalNotifs = allNotifications.filter(
        n => n.type === 'journal_reviewed' && !n.is_read
      );
      setJournalNotifications(journalNotifs);
      
      // Filter for checkin_reminder notifications that are unread
      const checkinNotifs = allNotifications.filter(
        n => n.type === 'checkin_reminder' && !n.is_read
      );
      setCheckinReminders(checkinNotifs);
      
      // Fetch unread messages
      await fetchUnreadMessages();
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, [fetchUnreadMessages]);

  useEffect(() => {
    fetchDashboardData();
    // Poll for new messages and notifications every 15 seconds
    const interval = setInterval(() => {
      fetchUnreadMessages();
      fetchNotifications();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchDashboardData, fetchUnreadMessages, fetchNotifications]);

  const markNotificationRead = async (notificationId, type) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`);
      // Remove from appropriate state
      if (type === 'journal_reviewed') {
        setJournalNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      } else if (type === 'checkin_reminder') {
        setCheckinReminders(prev => prev.filter(n => n.notification_id !== notificationId));
      }
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const handleJournalNotificationClick = async (notif) => {
    await markNotificationRead(notif.notification_id, 'journal_reviewed');
    navigate('/journals');
  };

  const handleCheckinReminderClick = async (notif) => {
    await markNotificationRead(notif.notification_id, 'checkin_reminder');
    navigate('/checkin');
  };

  const handleViewAllJournals = async () => {
    // Mark all journal notifications as read
    for (const notif of journalNotifications) {
      try {
        await axios.post(`${API}/notifications/${notif.notification_id}/read`);
      } catch (error) {
        console.error("Error marking notification read:", error);
      }
    }
    setJournalNotifications([]);
    navigate('/journals');
  };

  const handleGoToCheckin = async () => {
    // Mark all checkin reminders as read
    for (const notif of checkinReminders) {
      try {
        await axios.post(`${API}/notifications/${notif.notification_id}/read`);
      } catch (error) {
        console.error("Error marking notification read:", error);
      }
    }
    setCheckinReminders([]);
    navigate('/checkin');
  };

  const completionRate = stats && stats.total_assignments > 0 ? 
    Math.round(((stats.completed_assignments || 0) / stats.total_assignments) * 100) : 0;

  const getTimeAgo = (isoString) => {
    if (!isoString) return null;
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    return `${diffDays} days ago`;
  };

  const getActivityStatus = (isoString) => {
    if (!isoString) return "none";
    const diffDays = Math.floor((Date.now() - new Date(isoString).getTime()) / 86400000);
    if (diffDays <= 7) return "good";
    if (diffDays <= 14) return "warning";
    return "late";
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="student-dashboard" className="space-y-8">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-slate-600 mt-1">
              Continue your discipleship journey today.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {/* Streak Badge */}
            {streakData && streakData.current_streak > 0 && (
              <div 
                className="flex items-center gap-2 bg-gradient-to-r from-orange-100 to-amber-100 px-3 py-1.5 rounded-full cursor-pointer hover:from-orange-200 hover:to-amber-200 transition-colors"
                onClick={() => navigate('/checkin-history')}
              >
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="font-bold text-orange-700">{streakData.current_streak}</span>
                <span className="text-sm text-orange-600">week streak</span>
              </div>
            )}
            <Button 
              onClick={() => navigate('/checkin')}
              className="bg-green-600 hover:bg-green-700 rounded-full shadow-lg shadow-green-600/20"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              {checkInStatus?.has_submitted ? "Submit Another Check-In" : "Weekly Check-In"}
            </Button>
            <Button 
              onClick={() => navigate('/checkin-history')}
              variant="outline"
              className="rounded-full"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Check-In History
            </Button>
            <Button 
              onClick={() => navigate('/journals/new')}
              className="bg-amber-600 hover:bg-amber-700 rounded-full shadow-lg shadow-amber-600/20"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              New Journal Entry
            </Button>
          </div>
        </div>

        {/* Growth Focus - teacher/admin-set "one thing to work on" */}
        {user?.growth_focus && (
          <Card data-testid="growth-focus-card" className="border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50">
            <CardContent className="pt-6 flex items-start gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-teal-700" />
              </div>
              <div>
                <p className="text-xs font-medium text-teal-700 uppercase tracking-wide">Your Growth Focus</p>
                <p className="text-teal-900 mt-1">{user.growth_focus}</p>
                {user.growth_focus_context && (
                  <p className="text-sm text-teal-700/80 mt-2">{user.growth_focus_context}</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activity Pulse – last check-in & journal */}
        {(checkInStatus?.last_submitted_at || checkInStatus?.last_journal_at) && (
          <div
            data-testid="activity-pulse"
            className="flex flex-wrap gap-3"
          >
            {/* Last Check-In */}
            {(() => {
              const status = getActivityStatus(checkInStatus?.last_submitted_at);
              const timeAgo = getTimeAgo(checkInStatus?.last_submitted_at);
              const colors = {
                good: "bg-green-50 border-green-200 text-green-800",
                warning: "bg-amber-50 border-amber-200 text-amber-800",
                late: "bg-red-50 border-red-200 text-red-800",
                none: "bg-slate-50 border-slate-200 text-slate-600",
              };
              const dotColors = {
                good: "bg-green-500",
                warning: "bg-amber-500",
                late: "bg-red-500",
                none: "bg-slate-400",
              };
              return (
                <button
                  data-testid="activity-checkin-pill"
                  onClick={() => navigate('/checkin-history')}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border font-medium text-sm transition-all hover:scale-[1.02] hover:shadow-sm ${colors[status]}`}
                >
                  <ClipboardCheck className="w-4 h-4 shrink-0" />
                  <span className="text-xs font-semibold uppercase tracking-wide opacity-60">Check-In</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${dotColors[status]}`} />
                  <span className="font-semibold">{timeAgo ?? "Never submitted"}</span>
                </button>
              );
            })()}

            {/* Last Journal */}
            {(() => {
              const status = getActivityStatus(checkInStatus?.last_journal_at);
              const timeAgo = getTimeAgo(checkInStatus?.last_journal_at);
              const colors = {
                good: "bg-amber-50 border-amber-200 text-amber-800",
                warning: "bg-orange-50 border-orange-200 text-orange-800",
                late: "bg-red-50 border-red-200 text-red-800",
                none: "bg-slate-50 border-slate-200 text-slate-600",
              };
              const dotColors = {
                good: "bg-amber-500",
                warning: "bg-orange-500",
                late: "bg-red-500",
                none: "bg-slate-400",
              };
              return (
                <button
                  data-testid="activity-journal-pill"
                  onClick={() => navigate('/journals')}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-2xl border font-medium text-sm transition-all hover:scale-[1.02] hover:shadow-sm ${colors[status]}`}
                >
                  <BookOpen className="w-4 h-4 shrink-0" />
                  <span className="text-xs font-semibold uppercase tracking-wide opacity-60">Journal</span>
                  <span className={`w-1.5 h-1.5 rounded-full ${dotColors[status]}`} />
                  <span className="font-semibold">{timeAgo ?? "Never submitted"}</span>
                </button>
              );
            })()}
          </div>
        )}

        {/* Upcoming Meetings – Calendar Style */}
        <div data-testid="upcoming-meetings-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-lg font-bold text-slate-900">Upcoming Meetings</h2>
            <div className="flex items-center gap-2">
              {teacherId && (
                <Button
                  data-testid="dashboard-book-meeting-btn"
                  size="sm"
                  className="rounded-full bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20"
                  onClick={() => navigate(`/book-meeting/${teacherId}`)}
                >
                  <CalendarClock className="w-4 h-4 mr-1.5" />
                  Book a Meeting
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/meetings')}
                className="text-slate-500 hover:text-slate-700 rounded-full"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
          {upcomingMeetings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingMeetings.map((meeting) => {
                const dt = meeting.scheduled_at ? new Date(meeting.scheduled_at) : null;
                const isPending = meeting.status === 'pending_approval';
                const calColors = getMeetingCalColors(meeting, isPending);
                return (
                  <div
                    key={meeting.meeting_id}
                    data-testid={`dashboard-meeting-${meeting.meeting_id}`}
                    className={`group bg-white border rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 ${meeting.meeting_type === 'group' ? 'border-indigo-200 hover:border-indigo-400 ring-1 ring-indigo-100' : 'border-slate-200 hover:border-teal-300'}`}
                    onClick={() => navigate(`/meetings/${meeting.meeting_id}`)}
                  >
                    {meeting.meeting_type === 'group' && (
                      <div className="bg-indigo-600 px-4 py-1 flex items-center gap-1.5">
                        <Users className="w-3 h-3 text-indigo-200" />
                        <span className="text-[10px] font-bold text-white uppercase tracking-wider">Group Class</span>
                      </div>
                    )}
                    <div className="flex h-full">
                      {/* Calendar Icon Block */}
                      {dt && (
                        <div className={`w-20 shrink-0 flex flex-col items-center justify-center text-center ${calColors.bg}`}>
                          <span className={`text-[11px] font-bold uppercase tracking-wider ${calColors.label}`}>
                            {format(dt, 'EEE')}
                          </span>
                          <span className={`text-3xl font-extrabold leading-none mt-0.5 ${calColors.day}`}>
                            {format(dt, 'd')}
                          </span>
                          <span className={`text-[11px] font-semibold ${calColors.label}`}>
                            {format(dt, 'MMM')}
                          </span>
                        </div>
                      )}
                      {/* Meeting Info */}
                      <div className="flex-1 p-4 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900 truncate">{meeting.title}</p>
                          {isPending && (
                            <Badge variant="outline" className="text-[10px] h-4 shrink-0 bg-amber-50 text-amber-600 border-amber-200">
                              Pending
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          with {meeting.teacher_name || 'Teacher'}
                        </p>
                        {dt && (
                          <p className="text-lg font-bold text-slate-800 mt-2 leading-tight">
                            {format(dt, 'h:mm a')}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          {meeting.duration_minutes && (
                            <span className="text-[11px] text-slate-400">{meeting.duration_minutes} min</span>
                          )}
                          {meeting.zoom_link && (
                            <span className="flex items-center gap-0.5 text-[11px] text-blue-500">
                              <Video className="w-3 h-3" />
                              Zoom
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center">
              <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <CalendarClock className="w-7 h-7 text-teal-400" />
              </div>
              <p className="text-sm text-slate-500">No upcoming meetings</p>
              {teacherId && (
                <Button
                  data-testid="dashboard-book-meeting-btn-empty"
                  size="sm"
                  className="mt-3 rounded-full bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => navigate(`/book-meeting/${teacherId}`)}
                >
                  Book a Meeting
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Check-In Reminder from Teacher - Dismissible */}
        {checkinReminders.length > 0 && !checkInStatus?.has_submitted && (
          <Alert className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6 text-blue-400 hover:text-blue-600 hover:bg-blue-100"
              onClick={async () => {
                for (const notif of checkinReminders) {
                  await markNotificationRead(notif.notification_id, 'checkin_reminder');
                }
              }}
            >
              <span className="text-lg">×</span>
            </Button>
            <Bell className="h-5 w-5 text-blue-600" />
            <AlertTitle className="text-blue-800 font-semibold">
              Your teacher sent you a reminder
            </AlertTitle>
            <AlertDescription className="text-blue-700">
              <p className="mb-3">
                {checkinReminders[0]?.message || "Please complete your weekly check-in."}
              </p>
              <Button 
                onClick={handleGoToCheckin}
                className="bg-blue-600 hover:bg-blue-700"
                size="sm"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Complete Check-In Now
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Weekly Check-In Reminder - only show if no teacher reminder */}
        {checkInStatus && !checkInStatus.has_submitted && checkinReminders.length === 0 && (
          <Alert className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <AlertTitle className="text-amber-800 font-semibold">Weekly Check-In Reminder</AlertTitle>
            <AlertDescription className="text-amber-700">
              <p className="mb-3">
                You haven&apos;t submitted your weekly check-in yet. Take a few minutes to reflect on your discipleship progress.
              </p>
              <Button 
                onClick={() => navigate('/checkin')}
                className="bg-amber-600 hover:bg-amber-700"
                size="sm"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                Start Check-In
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Check-In Success Banner with Full Results */}
        {checkInStatus?.has_submitted && checkInStatus?.checkin && (
          <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <CardTitle className="text-lg font-semibold text-green-800">Latest Check-In</CardTitle>
                </div>
                <Badge className="bg-green-600">
                  {format(new Date(checkInStatus.checkin.created_at), 'MMM d')}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-white/70 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Loved God</p>
                  <p className="text-2xl font-bold text-green-700">{checkInStatus.checkin.loved_well?.loved_god}/10</p>
                </div>
                <div className="bg-white/70 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Loved People</p>
                  <p className="text-2xl font-bold text-green-700">{checkInStatus.checkin.loved_well?.loved_people}/10</p>
                </div>
                <div className="bg-white/70 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Fruits Avg</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {checkInStatus.checkin.fruits_of_spirit ? 
                      (Object.values(checkInStatus.checkin.fruits_of_spirit).reduce((a, b) => a + b, 0) / 9).toFixed(1) : '-'}/10
                  </p>
                </div>
                <div className="bg-white/70 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Glorifying God Avg</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {checkInStatus.checkin.glorifying_god ? 
                      (Object.values(checkInStatus.checkin.glorifying_god).reduce((a, b) => a + b, 0) / 5).toFixed(1) : '-'}/10
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/checkin')}
                className="rounded-full border-green-300 text-green-700 hover:bg-green-100"
              >
                View Full Details
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Unread Messages Alert - Prominent when there are messages */}
        {unreadMessages.length > 0 && (
          <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 overflow-hidden">
            <CardContent className="p-0">
              <div className="flex items-stretch">
                {/* Left indicator */}
                <div className="w-2 bg-blue-500 animate-pulse-soft"></div>
                
                <div className="flex-1 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Mail className="w-5 h-5 text-blue-600" />
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                          {unreadMessages.length}
                        </span>
                      </div>
                      <h3 className="font-semibold text-blue-900">New Messages</h3>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/messages')}
                      className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {unreadMessages.slice(0, 3).map((msg) => (
                      <div 
                        key={msg.message_id || `${msg.group_id}-${msg.created_at}`}
                        className="flex items-center gap-3 p-3 bg-white/60 rounded-xl cursor-pointer hover:bg-white transition-colors"
                        onClick={() => navigate(msg.is_group ? '/messages' : `/messages/${msg.sender_id}`)}
                      >
                        {msg.is_group ? (
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Users className="w-5 h-5 text-blue-600" />
                          </div>
                        ) : (
                          <Avatar className="w-10 h-10 flex-shrink-0">
                            <AvatarImage src={msg.sender_picture} />
                            <AvatarFallback className="bg-blue-100 text-blue-700">
                              {getInitials(msg.sender_name)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm">
                            {msg.is_group ? msg.group_name : msg.sender_name}
                          </p>
                          <p className="text-slate-600 text-sm truncate">
                            {msg.is_group ? `${msg.sender_name}: ` : ''}{msg.content || 'Attachment'}
                          </p>
                        </div>
                        <span className="text-xs text-slate-500 flex-shrink-0">
                          {format(new Date(msg.created_at), 'h:mm a')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Check-in Reminder Notifications */}
        {checkinReminders.length > 0 && (
          <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden">
            <CardContent className="p-0">
              <div className="flex items-stretch">
                {/* Left indicator */}
                <div className="w-2 bg-amber-500 animate-pulse"></div>
                
                <div className="flex-1 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Bell className="w-5 h-5 text-amber-600" />
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                          {checkinReminders.length}
                        </span>
                      </div>
                      <h3 className="font-semibold text-amber-900">Check-in Reminder</h3>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleGoToCheckin}
                      className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                    >
                      Complete Check-in
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {checkinReminders.slice(0, 2).map((notif) => (
                      <div 
                        key={notif.notification_id}
                        className="flex items-center gap-3 p-3 bg-white/60 rounded-xl cursor-pointer hover:bg-white transition-colors"
                        onClick={() => handleCheckinReminderClick(notif)}
                      >
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <ClipboardCheck className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm">
                            {notif.title}
                          </p>
                          <p className="text-slate-600 text-sm truncate">
                            {notif.message}
                          </p>
                        </div>
                        <span className="text-xs text-slate-500 flex-shrink-0">
                          {format(new Date(notif.created_at), 'MMM d')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Journal Reviewed Notifications */}
        {journalNotifications.length > 0 && (
          <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 overflow-hidden">
            <CardContent className="p-0">
              <div className="flex items-stretch">
                {/* Left indicator */}
                <div className="w-2 bg-green-500"></div>
                
                <div className="flex-1 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <BookOpen className="w-5 h-5 text-green-600" />
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                          {journalNotifications.length}
                        </span>
                      </div>
                      <h3 className="font-semibold text-green-900">Journal Reviewed</h3>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleViewAllJournals}
                      className="rounded-full border-green-300 text-green-700 hover:bg-green-100"
                    >
                      View Journals
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {journalNotifications.slice(0, 3).map((notif) => (
                      <div 
                        key={notif.notification_id}
                        className="flex items-center gap-3 p-3 bg-white/60 rounded-xl cursor-pointer hover:bg-white transition-colors"
                        onClick={() => handleJournalNotificationClick(notif)}
                      >
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm">
                            {notif.title}
                          </p>
                          <p className="text-slate-600 text-sm truncate">
                            {notif.message}
                          </p>
                        </div>
                        <span className="text-xs text-slate-500 flex-shrink-0">
                          {format(new Date(notif.created_at), 'MMM d')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Progress Card - Spans 8 cols on desktop */}
          <Card className="md:col-span-8 row-span-2 border-slate-100">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Your Progress</CardTitle>
              <CardDescription>Track your assignment completion</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-600">Assignment Completion</span>
                    <span className="text-sm font-semibold text-slate-900">{completionRate}%</span>
                  </div>
                  <Progress value={completionRate} className="h-3" />
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                  <div className="bg-slate-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-slate-900">{stats?.total_assignments || 0}</p>
                    <p className="text-xs text-slate-600 mt-1">Total Assignments</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-amber-700">{stats?.completed_assignments || 0}</p>
                    <p className="text-xs text-slate-600 mt-1">Completed</p>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-teal-700">{stats?.total_journals || 0}</p>
                    <p className="text-xs text-slate-600 mt-1">Journal Entries</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center relative">
                    <p className="text-2xl font-bold text-blue-700">{stats?.unread_messages || 0}</p>
                    <p className="text-xs text-slate-600 mt-1">Unread Messages</p>
                    {(stats?.unread_messages || 0) > 0 && (
                      <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    className="justify-start h-auto py-3 px-4 rounded-xl"
                    onClick={() => navigate('/assignments')}
                  >
                    <FileText className="w-5 h-5 mr-3 text-slate-600" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">Assignments</p>
                      <p className="text-xs text-slate-500">View all tasks</p>
                    </div>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="justify-start h-auto py-3 px-4 rounded-xl"
                    onClick={() => navigate('/journals')}
                  >
                    <BookOpen className="w-5 h-5 mr-3 text-amber-600" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">Journals</p>
                      <p className="text-xs text-slate-500">Your entries</p>
                    </div>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="justify-start h-auto py-3 px-4 rounded-xl"
                    onClick={() => navigate('/forum')}
                  >
                    <MessageSquare className="w-5 h-5 mr-3 text-teal-600" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">Forum</p>
                      <p className="text-xs text-slate-500">Join discussion</p>
                    </div>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sidebar Cards - Span 4 cols */}
          <Card className={`md:col-span-4 border-slate-100 ${unreadMessages.length > 0 ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50' : ''}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center">
                <Mail className={`w-4 h-4 mr-2 ${unreadMessages.length > 0 ? 'text-blue-600' : 'text-slate-600'}`} />
                Messages
                {unreadMessages.length > 0 && (
                  <Badge className="ml-2 bg-blue-600">{unreadMessages.length} new</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {unreadMessages.length > 0 ? (
                <div className="space-y-2">
                  {unreadMessages.slice(0, 3).map((msg) => (
                    <div 
                      key={msg.message_id || `${msg.group_id}-${msg.created_at}`}
                      className="flex items-center gap-3 p-2 bg-white/70 rounded-lg cursor-pointer hover:bg-white transition-colors"
                      onClick={() => navigate(msg.is_group ? '/messages' : `/messages/${msg.sender_id}`)}
                    >
                      {msg.is_group ? (
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-blue-600" />
                        </div>
                      ) : (
                        <Avatar className="w-8 h-8 flex-shrink-0">
                          <AvatarImage src={msg.sender_picture} />
                          <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                            {msg.sender_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {msg.is_group ? msg.group_name : msg.sender_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {msg.is_group ? `${msg.sender_name}: ` : ''}{msg.content || 'Attachment'}
                        </p>
                      </div>
                      <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                    </div>
                  ))}
                  <Button 
                    variant="link" 
                    className="w-full text-blue-600 mt-2"
                    onClick={() => navigate('/messages')}
                  >
                    View All Messages <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="text-center py-2">
                  <p className="text-3xl font-bold text-slate-900">0</p>
                  <p className="text-xs text-slate-500 mt-1">Unread</p>
                  <Button 
                    variant="link" 
                    className="mt-2 text-slate-600"
                    onClick={() => navigate('/messages')}
                  >
                    View Messages <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="md:col-span-4 border-slate-100">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center">
                <Sparkles className="w-4 h-4 mr-2 text-amber-600" />
                AI Bible Verse
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button 
                variant="outline" 
                className="w-full rounded-xl"
                onClick={() => navigate('/forum?tab=bible_verses')}
              >
                Get Verse Suggestion
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Assignments */}
        <Card className="border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Recent Assignments</CardTitle>
              <Button variant="link" onClick={() => navigate('/assignments')}>
                View All <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {assignments.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No assignments yet</p>
            ) : (
              <div className="space-y-3">
                {assignments.map((assignment) => (
                  <div 
                    key={assignment.assignment_id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/assignments/${assignment.assignment_id}`)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <FileText className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{assignment.title}</p>
                        <p className="text-xs text-slate-500">
                          {assignment.due_date ? (
                            <>Due: {format(new Date(assignment.due_date), 'MMM d, yyyy')}</>
                          ) : (
                            'No due date'
                          )}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
